<script setup lang="ts">
  import { computed, ref, useTemplateRef } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useIntersectionObserver } from '@vueuse/core'
  import { useGoogleAnalytics, useMediaQueryDetector } from '@/shared/composables'
  import { useUserStore } from '@/shared/store'
  import { LOAD_EVENT_NAME, PROCEED_EVENT_NAME } from './FlowSelectPlanWidget.constants'
  import { type Locale } from '@/shared/types/common.types'
  import type { CoverModalData, Props } from './FlowSelectPlanWidget.types'
  import LinkQC from '@qicgroupdoha/qic-connect-ds/components/LinkQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import PlanV2 from './components/PlanV2/PlanV2.vue'
  import PlanV2Covers from './components/PlanV2Covers/PlanV2Covers.vue'
  import { SchemeType, type Plan } from '@/shared/types/api.types'
  import FloatingPlan from './components/FloatingPlan/FloatingPlan.vue'
  import CoversPopup from './components/CoversPopup/CoversPopup.vue'
  import { toFixedNumber, formatDateDefault } from '@/shared/utils'
  import SinglePlanV2 from './components/SinglePlanV2/SinglePlanV2.vue'
  import { useCovers } from './composables/useCovers/useCovers'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>): void
    (e: 'back'): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()

  const { t, locale } = useI18n()
  const gaService = useGoogleAnalytics()
  const userStore = useUserStore()
  const adultsCount = computed(() => userStore.adultsCount)
  const childrenCount = computed(() => userStore.childrenCount)
  const travelersCount = adultsCount.value + childrenCount.value

  const departureDate = ref<Date | null>(userStore.departureDate)
  const returnDate = ref<Date | null>(userStore.returnDate)
  const { isMediaQueryDetected: isTablet } = useMediaQueryDetector('(max-width: 768px)')
  const isCoversPopupShown = ref(false)

  const activeTab = ref<'worldwideexcl' | 'flyeurope'>('worldwideexcl')
  const isFloatingPlans = ref(false)
  const isFloatingButton = ref(false)
  const isSingleTrip = computed(() => userStore.tripType === 'SINGLE')

  const plansBlockRef = useTemplateRef<HTMLElement | null>('plansBlockRef')

  useIntersectionObserver(
    plansBlockRef,
    ([entry]) => {
      if (isTablet.value || isFlyEurope.value || !entry.boundingClientRect.height) return
      isFloatingPlans.value = !entry.isIntersecting
    },
    { rootMargin: '-150px 0px 0px 0px' }
  )

  useIntersectionObserver(
    plansBlockRef,
    ([entry]) => {
      if (!isTablet.value || !entry.boundingClientRect.height) return
      isFloatingButton.value = !entry.isIntersecting
    },
    { rootMargin: '-500px 0px 0px 0px' }
  )

  const plansSchemeTypeCodes = computed(() => props.plans.map((plan) => plan.schemeTypeCode))

  const onProceed = () => {
    emit(PROCEED_EVENT_NAME)
  }

  const destinationText = computed(() => {
    const ids = userStore.destination.map((el) => el.id)
    return props.listOfDestionations
      .filter((el) => ids.includes(el.id))
      .map((el) => el.name)
      .join(', ')
  })

  const informationCardOptions = computed(() => [
    {
      id: 'date',
      content: `${formatDateDefault(departureDate.value!, locale.value as Locale)} - ${formatDateDefault(returnDate.value!, locale.value as Locale)}`,
    },
    {
      id: 'travelers',
      content: `${t(
        'outbound.select_plan.insurance_values.travelers',
        { travelersCount },
        travelersCount
      )}`,
    },
  ])

  const isSinglePlan = computed(() => props.plans.length === 1)

  const hasType = (type: SchemeType) => plansSchemeTypeCodes.value.includes(type)

  const isWorldwide = computed(() => hasType(SchemeType.Worldwide))
  const isWorldwideExcl = computed(() => hasType(SchemeType.WorldwideExcl))
  const isRegional = computed(() => hasType(SchemeType.Regional))
  const isFlyEurope = computed(() => hasType(SchemeType.FlyEurope))
  const isFlyEuropeActive = computed(() => activeTab.value === 'flyeurope')

  const isCoverdBlockShown = computed(
    () => isWorldwideExcl.value && (isWorldwide.value || isRegional.value)
  )

  const isTabsShown = computed(() => isWorldwideExcl.value && isFlyEurope.value)

  const { covers } = useCovers(isFlyEuropeActive, isFlyEurope, isRegional)
  const { covers: coversModal } = useCovers(isFlyEuropeActive, isFlyEurope, isRegional, true)

  const handleSelectPlan = async (plan: Plan) => {
    const countries = userStore.destination.map((el) => el.id)

    userStore.setSelectedPlan(plan.schemecode, true)
    gaService.sendOutboundSelectPlanProceed(
      isSingleTrip.value,
      plan,
      countries,
      String(adultsCount.value + childrenCount.value),
      plan.coinsDiscountValue ? toFixedNumber(plan.coinsDiscountValue) : undefined
    )
    onProceed()
  }

  const openPopup = (tab?: 'worldwideexcl' | 'flyeurope') => {
    isCoversPopupShown.value = true
    if (tab) activeTab.value = tab
    gaService.sendClickSelectPlanLearnMore()
  }

  const getVisibleCovers = (covers: CoverModalData[]) => covers.filter((cover) => !cover?.hide)

  const handleClickWhatsCovered = () => {
    const element = document.getElementById('covers-block')

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      })
    }
    gaService.sendClickSelectPlanWhatsCovered()
  }

  const handleClickBackToPlans = () => {
    gaService.sendClickSelectPlanBackToPlans()
  }
</script>

<template>
  <div class="flow-select-plan-widget-v3 ds-plan">
    <FlowCardQC
      id="plans-block"
      :title="
        isSinglePlan
          ? t('outbound.select_plan.insurance_values.header_one_plan')
          : t('outbound.select_plan.insurance_values.header_two_plans')
      "
      :with-button="false"
      :with-back-button="withBackButton"
      :class="{ 'flow-card--wide': plans.length > 1 }"
      class="flow-card-qc-v2"
      @back="emit('back')"
    >
      <template #header>
        <div class="text-regular general-information" data-test="information-card-content">
          {{ destinationText }} • {{ informationCardOptions[0].content }} •
          <span class="general-information__travelers">{{
            informationCardOptions[1].content
          }}</span>
        </div>
      </template>

      <div ref="plansBlockRef" class="flow-select-plan-widget__plans">
        <template v-if="isSinglePlan">
          <SinglePlanV2
            :plan="plans[0]"
            :travelers-count="travelersCount"
            :policy-wording-link="policyWording"
            @select-plan="handleSelectPlan"
          />
        </template>
        <template v-else>
          <PlanV2
            v-for="plan in plans"
            :key="plan.schemeTypeCode"
            :plan="plan"
            :plans-scheme-type-codes="plansSchemeTypeCodes"
            :travelers-count="travelersCount"
            @select-plan="handleSelectPlan"
          >
            <template v-if="isFlyEurope" #after-content>
              <div class="plan-details">
                <div
                  v-for="(item, itemIndex) in covers"
                  :key="item.title"
                  class="plan-details__item"
                >
                  <div class="plan-details__header">
                    <div
                      class="plan-card__after-content-item-title heading-6"
                      :class="{
                        'plan-details__item--strikethrough':
                          item?.strikethrough && plan.schemeTypeCode === SchemeType.FlyEurope,
                      }"
                    >
                      {{ $t(item.title) }}
                    </div>
                    <div v-if="!item.strikethrough && itemIndex === 0" class="text-small-regular">
                      {{ $t('outbound.select_plan.insurance_values.payout_limit') }}
                    </div>
                  </div>
                  <ul class="plan-details__list">
                    <li
                      v-for="cover in getVisibleCovers(item.cover)"
                      :key="cover.name"
                      class="plan-details__cover-item"
                      :class="{
                        'plan-details__cover-item--strikethrough':
                          plan.schemeTypeCode === SchemeType.FlyEurope &&
                          (cover?.strikethrough || item?.strikethrough),
                      }"
                    >
                      <div class="text-small-regular plan-details__cover-item-title">
                        {{ $t(cover.name) }}
                        <IconQC
                          v-if="cover.tooltip"
                          v-tooltip-qc="{
                            title: $t(cover.tooltip),
                            position: 'bottom',
                            maxWidth: 210,
                          }"
                          name="info"
                          color="var(--colorIcnSrfTertiary)"
                          size="sm"
                        />
                      </div>
                      <div class="text-small-regular">
                        {{
                          plan.schemeTypeCode === SchemeType.FlyEurope
                            ? $t(cover.priceFlyEurope || '')
                            : $t(cover.price)
                        }}
                      </div>
                    </li>
                  </ul>
                </div>
                <!-- @vue-ignore -->
                <LinkQC :underline="false" @click="() => openPopup(plan.schemeTypeCode)">{{
                  $t('qic.common.learn_more')
                }}</LinkQC>
              </div>
            </template>
          </PlanV2>
        </template>
      </div>

      <ButtonQC
        v-if="!isTablet && !isFlyEurope && !isSinglePlan"
        variant="secondary"
        class="select-plan__button"
        size="medium"
        @click="handleClickWhatsCovered"
      >
        {{ $t('travel.outbound.select_plan.whats_covered_button') }}
        <IconQC name="arrow-narrow-down" color="var(--colorIcnSrfAction)" size="lg" />
      </ButtonQC>
    </FlowCardQC>

    <FlowCardQC v-if="isCoverdBlockShown" id="covers-block" class="flow-card--wide flow-card-qc-v2">
      <PlanV2Covers
        :plans-scheme-type-codes="plansSchemeTypeCodes"
        @click-learn-more="isCoversPopupShown = true"
      />
      <template #button>
        <div
          :class="{
            'select-plan__floating': isTablet && isFloatingButton,
          }"
        >
          <ButtonQC
            variant="secondary"
            size="medium"
            href="#plans-block"
            class="select-plan__button"
            :wide="isTablet"
            @click="handleClickBackToPlans"
          >
            {{ $t('travel.outbound.select_plan.back_to_plans') }}
            <IconQC name="arrow-narrow-up" color="var(--colorIcnSrfAction)" size="lg" />
          </ButtonQC>
        </div>
      </template>
    </FlowCardQC>

    <transition v-if="!isTablet" name="fade">
      <div v-if="isFloatingPlans" class="flow-select-plan-widget__plans-floating">
        <FloatingPlan
          v-for="plan in plans"
          :key="plan.schemeTypeCode"
          :plan="plan"
          :plans-scheme-type-codes="plansSchemeTypeCodes"
          @select-plan="handleSelectPlan"
        />
      </div>
    </transition>

    <!-- @vue-ignore -->
    <CoversPopup
      v-if="isCoversPopupShown"
      v-model="activeTab"
      :policy-wording-link="policyWording"
      :data="coversModal"
      :with-tabs="isTabsShown"
      data-test="covers-popup"
      @close="isCoversPopupShown = false"
    />
  </div>
</template>

<style scoped lang="scss">
  @use '@qicgroupdoha/qic-connect-ds/scss/typography';
  @use './FlowSelectPlanWidget';
  @use '@/shared/assets/styles/utils';

  .flow-select-plan-widget {
    &__plans {
      grid-template-columns: repeat(auto-fit, minmax(47%, 1fr));
      gap: var(--spacing-5);
      position: relative;

      /* stylelint-disable-next-line */
      @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: var(--spacing-8);
      }
    }
  }

  .general-information__travelers {
    display: inline-block;
  }

  .select-plan__button {
    display: block !important;
    margin: 0 auto;
    width: fit-content;
    margin-top: var(--spacing-8);
  }

  .flow-card-qc-v2 {
    scroll-margin-top: var(--spacing-8);
    padding: var(--spacing-8);

    /* stylelint-disable-next-line */
    @media (max-width: 768px) {
      padding: var(--spacing-7);
      border-radius: var(--radius-7);
    }

    @include utils.mobile-flow {
      padding-inline: var(--spacing-5);
    }
  }

  .flow-select-plan-widget__plans-floating {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-4);
    position: fixed;
    top: 0;
    z-index: 99;
    padding: var(--spacing-7);
    background-color: var(--colorBgSrfPrimary);
    border-radius: var(--spacing-7);
    max-width: 960px;
    left: 0;
    right: 0;
    margin: 0 auto;
    box-shadow: var(--shadowFloating);
  }

  .select-plan__floating {
    position: fixed;
    z-index: 99;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0 auto;
    padding: var(--spacing-4);
    background-color: var(--colorBgSrfPrimary);
    border-radius: var(--spacing-7) var(--spacing-7) 0 0;
    box-shadow: var(--shadowBold);
    display: flex;
    justify-content: center;
    align-items: center;

    .select-plan__button {
      margin-top: 0;
    }
  }

  .flow-select-plan-widget-v3 {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-7);

    /* stylelint-disable-next-line */
    @media (max-width: 768px) {
      gap: var(--spacing-6);
    }
  }

  .plan-details {
    margin-top: var(--spacing-7);
    padding-top: var(--spacing-7);
    border-top: 1px solid var(--colorBrdrSrfSecondary);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-6);
  }

  .plan-details__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-3);
  }

  .plan-details__list {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2-5);
  }

  .plan-details__cover-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .plan-details__cover-item-title {
    display: flex;
    align-items: center;
    gap: var(--spacing-1);
  }

  .plan-details__cover-item--strikethrough {
    color: var(--colorTxtSrfGhost);
    text-decoration-line: line-through;
  }

  .plan-details__item--strikethrough {
    color: var(--colorTxtSrfGhost);
  }

  .flow-card-qc-v2:has(.single-plan-v2) {
    padding-bottom: 0;
  }
</style>

<style>
  .flow-view-v2:has(.select-plan__floating) {
    /* qic-connect-ignore-next-line */
    padding-bottom: 70px;
  }
</style>
